import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { allProjects } from './Portfolio';

const PortfolioDetail = () => {
  const { id } = useParams();
  const project = allProjects.find(p => p.id === id);

  if (!project) {
    return (
      <div className="portfolio-detail-page">
        <div className="container">
          <h1>Project not found</h1>
          <Link to="/portfolio">Back to Portfolio</Link>
        </div>
      </div>
    );
  }

  // Get related projects (same category, excluding current)
  const relatedProjects = allProjects
    .filter(p => p.category === project.category && p.id !== project.id)
    .slice(0, 3);

  return (
    <div className="portfolio-detail-page">
      {/* Hero Title */}
      <section className="portfolio-detail-hero">
        <div className="container">
          <motion.h1 
            className="portfolio-detail-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {project.title}
          </motion.h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="portfolio-detail-content">
        <div className="container">
          <div className="portfolio-detail-grid">
            {/* Left - Description */}
            <motion.div 
              className="portfolio-detail-left"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="portfolio-detail-subtitle">{project.description}</h2>
              <p className="portfolio-detail-text">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore
              </p>
              <ul className="portfolio-detail-features">
                <li>Dolore magna</li>
                <li>Dabore et dolore magna</li>
                <li>Sunt in culpa qui officia</li>
              </ul>
              <p className="portfolio-detail-description">
                Fusce vitae lacinia nisi. Duis posuere, nisi ac suscipit feugiat, enim massa pellentesque enim, 
                a tincidunt sapien quam quis tortor. Mauris non orci ipsum. Ut ac sem orci. Nulla sit amet nibh 
                placerat, dignissim risus ac, pretium ligula.
              </p>
            </motion.div>

            {/* Right - Project Info */}
            <motion.div 
              className="portfolio-detail-right"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="portfolio-detail-info">
                <div className="portfolio-info-item">
                  <span className="portfolio-info-label">Client</span>
                  <span className="portfolio-info-value">{project.client}</span>
                </div>
                <div className="portfolio-info-item">
                  <span className="portfolio-info-label">Project year</span>
                  <span className="portfolio-info-value">{project.year}</span>
                </div>
                <div className="portfolio-info-item">
                  <span className="portfolio-info-label">Location</span>
                  <span className="portfolio-info-value">{project.location}</span>
                </div>
                <div className="portfolio-info-item">
                  <span className="portfolio-info-label">Designer</span>
                  <span className="portfolio-info-value">{project.designer}</span>
                </div>
              </div>

              <div className="portfolio-detail-share">
                <span className="share-label">Share media</span>
                <div className="share-icons">
                  <a href="#" aria-label="Share on Facebook">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                    </svg>
                  </a>
                  <a href="#" aria-label="Share on Twitter">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a href="#" aria-label="Share on LinkedIn">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                      <rect x="2" y="9" width="4" height="12"/>
                      <circle cx="4" cy="4" r="2"/>
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Project Image */}
      <section className="portfolio-detail-image">
        <motion.img 
          src={project.image} 
          alt={project.title}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        />
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="portfolio-related">
          <div className="container">
            <motion.h2 
              className="portfolio-related-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Related project
            </motion.h2>
            <div className="portfolio-related-grid">
              {relatedProjects.map((relatedProject, index) => (
                <motion.div 
                  key={relatedProject.id}
                  className="portfolio-related-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/portfolio/${relatedProject.id}`}>
                    <div className="portfolio-related-image">
                      <img src={relatedProject.image} alt={relatedProject.title} />
                      <div className="portfolio-related-overlay"></div>
                    </div>
                    <div className="portfolio-related-content">
                      <span className="portfolio-related-category">{relatedProject.category}</span>
                      <h3 className="portfolio-related-name">{relatedProject.title}</h3>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PortfolioDetail;
